/* Pure helpers for the Command Bar v2.
   No React/DOM imports so this can be unit-tested cheaply. */

export const GLOBAL_ACTIONS = [
    { id: 'open-hub', label: 'Open Workspace Hub' },
    { id: 'toggle-theme', label: 'Toggle Porcelain / Graphite' },
];

const KIND_LABEL = {
    page: 'Page',
    action: 'Action',
    contact: 'Contact',
    company: 'Company',
    deal: 'Deal',
    record: 'Record',
};

function score(text, query) {
    if (!query) return 1;
    const t = (text || '').toLowerCase();
    const q = query.toLowerCase();
    if (!t) return 0;
    if (t.startsWith(q)) return 3;
    // Word start match
    if (new RegExp(`(^|\\s|[/_-])${escapeRegExp(q)}`).test(t)) return 2;
    if (t.includes(q)) return 1;
    return 0;
}

function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function best(...values) {
    let m = 0;
    for (const v of values) if (v > m) m = v;
    return m;
}

export function searchPages(query, allItems) {
    if (!allItems || !allItems.length) return [];
    if (!query) {
        return allItems.slice(0, 6).map(entry => ({
            kind: 'page',
            score: 1,
            id: `page:${entry.workspace.id}:${entry.item.id}`,
            label: entry.item.label,
            sub: `${entry.workspace.name} / ${entry.section.label}`,
            wsId: entry.workspace.id,
            path: entry.item.path,
            entry,
        }));
    }
    const results = [];
    for (const entry of allItems) {
        const s = best(
            score(entry.item.label, query),
            score(entry.section.label, query),
            score(entry.workspace.name, query),
        );
        if (s > 0) {
            results.push({
                kind: 'page',
                score: s,
                id: `page:${entry.workspace.id}:${entry.item.id}`,
                label: entry.item.label,
                sub: `${entry.workspace.name} / ${entry.section.label}`,
                wsId: entry.workspace.id,
                path: entry.item.path,
                entry,
            });
        }
    }
    return results.sort((a, b) => b.score - a.score);
}

export function searchActions(query, activeWorkspaces) {
    const items = [];
    for (const ws of activeWorkspaces || []) {
        for (const action of ws.quickActions || []) {
            const s = score(action.label, query) || score(ws.name, query);
            if (!query || s > 0) {
                items.push({
                    kind: 'action',
                    score: s || 1,
                    id: `action:${ws.id}:${action.id}`,
                    label: action.label,
                    sub: `${ws.name} · quick action`,
                    wsId: ws.id,
                    path: action.path,
                    handler: null,
                });
            }
        }
    }
    for (const global of GLOBAL_ACTIONS) {
        const s = score(global.label, query);
        if (!query || s > 0) {
            items.push({
                kind: 'action',
                score: s || 1,
                id: `action:${global.id}`,
                label: global.label,
                sub: 'Global',
                handler: global.id,
            });
        }
    }
    return items.sort((a, b) => b.score - a.score);
}

export function searchRecords(query, records) {
    if (!records || !records.length) return [];
    if (!query) return [];
    const results = [];
    for (const r of records) {
        const s = best(score(r.label, query), score(r.sub, query));
        if (s > 0) {
            results.push({
                kind: r.kind || 'record',
                score: s,
                id: `${r.kind || 'record'}:${r.id}`,
                label: r.label,
                sub: r.sub,
                wsId: r.wsId,
                path: r.path,
            });
        }
    }
    return results.sort((a, b) => b.score - a.score);
}

export function highlight(label, query) {
    if (!query) return label;
    const idx = (label || '').toLowerCase().indexOf(query.toLowerCase());
    if (idx < 0) return label;
    return {
        before: label.slice(0, idx),
        match: label.slice(idx, idx + query.length),
        after: label.slice(idx + query.length),
    };
}

const MAX_ROWS = 14;
const CAP_PAGES = 6;
const CAP_RECORDS = 6;
const CAP_ACTIONS = 6;
const CAP_RECENTS = 5;

/**
 * Build the ordered result set for the palette.
 * @returns {Array<{kind:string, key:string, label:string, sub:string, wsId?:string, path?:string, handler?:string}>}
 */
export function buildResults({ query, pages, actions, records, recents }) {
    const q = (query || '').trim();
    const rows = [];

    // Assistant row is rendered by the palette component; not included here so
    // keyboard nav can treat it as a distinct row.

    if (!q) {
        // Recents first, then top pages
        const recentRows = (recents || []).slice(0, CAP_RECENTS).map((r, i) => ({
            kind: r.kind || 'page',
            key: `recent:${r.path || r.id || i}`,
            label: r.label,
            sub: r.sub || r.wsName || KIND_LABEL[r.kind] || 'Recent',
            wsId: r.wsId,
            path: r.path,
            handler: r.handler,
            groupKey: 'recent',
        }));
        rows.push(...recentRows);
        // Show a peek of pages when we don't have enough recents to fill the list
        if (rows.length < MAX_ROWS) {
            const remain = MAX_ROWS - rows.length;
            const pageRows = (pages || []).slice(0, remain).map(p => ({
                kind: 'page',
                key: p.id,
                label: p.label,
                sub: p.sub,
                wsId: p.wsId,
                path: p.path,
                groupKey: 'page',
            }));
            rows.push(...pageRows);
        }
        // Cap at MAX_ROWS
        return { rows: rows.slice(0, MAX_ROWS), groups: groupLabelsFor(rows.slice(0, MAX_ROWS)) };
    }

    const pageRows = (pages || []).slice(0, CAP_PAGES).map(p => ({
        kind: 'page',
        key: p.id,
        label: p.label,
        sub: p.sub,
        wsId: p.wsId,
        path: p.path,
        groupKey: 'page',
    }));
    const recordRows = (records || []).slice(0, CAP_RECORDS).map(r => ({
        kind: r.kind,
        key: r.id,
        label: r.label,
        sub: r.sub,
        wsId: r.wsId,
        path: r.path,
        groupKey: 'record',
    }));
    const actionRows = (actions || []).slice(0, CAP_ACTIONS).map(a => ({
        kind: 'action',
        key: a.id,
        label: a.label,
        sub: a.sub,
        wsId: a.wsId,
        path: a.path,
        handler: a.handler,
        groupKey: 'action',
    }));

    rows.push(...pageRows, ...recordRows, ...actionRows);
    const capped = rows.slice(0, MAX_ROWS);
    return { rows: capped, groups: groupLabelsFor(capped) };
}

function groupLabelsFor(rows) {
    const g = { recent: null, page: null, record: null, action: null };
    for (let i = 0; i < rows.length; i++) {
        const key = rows[i].groupKey;
        if (g[key] == null) g[key] = i;
    }
    const labels = { recent: 'Recent', page: 'Pages', record: 'Records', action: 'Actions' };
    return Object.entries(g)
        .filter(([, i]) => i != null)
        .map(([key, i]) => ({ key, at: i, label: labels[key] }))
        .sort((a, b) => a.at - b.at);
}
